import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { IDM_METHODS } from 'client/data/models/profile/profile-constants';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';

import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { EditPersonalInfo } from 'site-modules/profile/components/settings/edit-personal-info/edit-personal-info';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { PrivacyRequest } from 'site-modules/profile/components/settings/privacy-request/privacy-request';
import { ChangePassword } from 'site-modules/profile/components/settings/change-password/change-password';
import { EmailSubscriptions } from 'site-modules/profile/components/settings/email-subscriptions/email-subscriptions';
import { DeleteAccount } from 'site-modules/profile/components/settings/delete-account/delete-account';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

import './settings-landing.scss';

export function SettingsLandingComponent({
  isMobile = false,
  isDeletedUser = false,
  profileData = null,
  id = null,
  session = null,
  location = null,
  privacy = null,
  pageContext = {},
  setModelValue,
}) {
  const [showActiveSpinner, toggleActiveSpinner] = useToggle(false);
  const [currentProfileData, setCurrentProfileData] = useState(profileData);
  const prevProfileData = usePrevious(profileData);

  if (prevProfileData !== profileData && !isDeletedUser) {
    setCurrentProfileData(profileData);
  }

  const saveChanges = useCallback(
    (data, method = IDM_METHODS.UPDATE) => {
      toggleActiveSpinner();
      return setModelValue('data.idm', UserProfileModel, {
        data,
        visitorAndPageContext: { visitor: { id, session, location }, pageContext },
        method,
      }).finally(() => {
        toggleActiveSpinner();
      });
    },
    [pageContext, setModelValue, toggleActiveSpinner, id, session, location]
  );

  const props = {
    isMobile,
    profileData: currentProfileData,
    isDeletedUser,
    saveChanges,
    showActiveSpinner,
  };

  return (
    <div className="dashboard-default-spacing">
      {currentProfileData && (
        <Row>
          <Col className="profile-settings text-gray-darker" xs={12} lg={10} xl={8}>
            <EditPersonalInfo {...props} wrapperClassName="edit-personal-info-form mb-2" captionText="Personal Info" />
            <FeatureFlag name="enableGpc">
              <PrivacyRequest
                {...props}
                wrapperClassName="mb-2"
                privacy={privacy}
                captionText="Privacy Requests"
                isEditable={false}
              />
            </FeatureFlag>
            <ChangePassword {...props} wrapperClassName="change-password-form mb-2" captionText="Password" />
            <EmailSubscriptions
              {...props}
              wrapperClassName="email-subscriptions-form mb-2"
              captionText="Email Subscriptions"
            />
            <DeleteAccount
              {...props}
              wrapperClassName="delete-account-form mb-2"
              captionText="Delete Your Edmunds Account"
              editButtonText="start"
            />
          </Col>
        </Row>
      )}
    </div>
  );
}

SettingsLandingComponent.propTypes = {
  isMobile: PropTypes.bool,
  profileData: PropTypes.shape({}),
  setModelValue: PropTypes.func.isRequired,
  id: PropTypes.string,
  session: PropTypes.string,
  location: PropTypes.shape({}),
  privacy: PropTypes.shape({
    uspString: PropTypes.string,
    doNotSell: PropTypes.bool,
  }),
  pageContext: PropTypes.shape({}),
  isDeletedUser: PropTypes.bool,
};

const stateToPropsConfig = {
  profileData: bindToPath('data.idm', UserProfileModel),
};

const mapStateToProps = ({ visitor, pageContext, profile }) => ({
  id: visitor?.id,
  session: visitor?.session,
  location: visitor?.location,
  privacy: visitor?.privacy,
  pageContext,
  isDeletedUser: profile?.isDeletedUser,
});

export const SettingsLanding = connect(mapStateToProps)(connectToModel(SettingsLandingComponent, stateToPropsConfig));
